import { logger } from "../utils/logger";
import { useState, useEffect } from 'react';
import { X, Zap, ArrowRight, Sparkles, TrendingUp, AlertCircle, Calendar, BarChart3 } from 'lucide-react';
import { Link } from 'react-router';
import { API_BASE_URL } from '../utils/apiConfig';
import { CountdownTimer } from './CountdownTimer';

// Icon mapping
const ICON_MAP: Record<string, any> = {
  'Zap': Zap,
  'Sparkles': Sparkles,
  'TrendingUp': TrendingUp,
  'AlertCircle': AlertCircle,
  'Calendar': Calendar,
  'BarChart3': BarChart3
};

interface PromoBarData {
  content: {
    icon: string;
    heading: string;
    subtext: string;
    ctaText: string;
    ctaLink: string;
  };
  visibility: {
    showIcon: boolean;
    showHeading: boolean;
    showSubtext: boolean;
    showCountdown: boolean;
    showCTA: boolean;
  };
  countdown: {
    enabled: boolean;
    endDate: string | null;
    timezone: string;
    labels: {
      days: string;
      hours: string;
      minutes: string;
      seconds: string;
    };
    hideWhenExpired: boolean;
  };
  styling: {
    backgroundGradient: {
      from: string;
      via: string;
      to: string;
    };
    textColor: string;
    accentColor: string;
  };
  behavior: {
    dismissible: boolean;
    dismissalLimit: number;
    cooldownHours: number;
    hideAfterClick: boolean;
  };
}

export function AnnouncementBanner() {
  const [promoData, setPromoData] = useState<PromoBarData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasTrackedView, setHasTrackedView] = useState(false);

  // Load promo bar data from API
  useEffect(() => {
    loadPromoBar();
  }, []);

  // Track view when visible
  useEffect(() => {
    if (isVisible && promoData && !hasTrackedView) {
      trackEvent('view');
      setHasTrackedView(true);
    }
  }, [isVisible, promoData, hasTrackedView]);

  const loadPromoBar = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/promo-bar/active`);
      const data = await response.json();
      
      if (data.success && data.data) {
        // Migrate old data to include new properties
        const migratedData = {
          ...data.data,
          // Add visibility defaults if missing
          visibility: data.data.visibility || {
            showIcon: true,
            showHeading: true,
            showSubtext: true,
            showCountdown: false,
            showCTA: true
          },
          // Add countdown defaults if missing
          countdown: data.data.countdown || {
            enabled: false,
            endDate: null,
            timezone: 'America/New_York',
            labels: {
              days: 'd',
              hours: 'h',
              minutes: 'm',
              seconds: 's'
            },
            hideWhenExpired: true
          }
        };
        
        setPromoData(migratedData);
        checkVisibility(migratedData);
      } else {
        setIsVisible(false);
      }
    } catch (error) {
      logger.error('Error loading promo bar:', error);
      setIsVisible(false);
    }
  };

  const checkVisibility = (data: PromoBarData) => {
    // Check if user clicked the CTA (and hideAfterClick is enabled)
    if (data.behavior.hideAfterClick) {
      const clickedCTA = localStorage.getItem('promo_bar_clicked');
      if (clickedCTA === 'true') {
        setIsVisible(false);
        return;
      }
    }

    // Check if banner was permanently dismissed
    const dismissalCount = parseInt(localStorage.getItem('promo_bar_dismissal_count') || '0');
    if (dismissalCount >= data.behavior.dismissalLimit) {
      setIsVisible(false);
      return;
    }

    // Check if banner was temporarily dismissed (within cooldown period)
    const dismissedTimestamp = localStorage.getItem('promo_bar_dismissed_timestamp');
    if (dismissedTimestamp) {
      const dismissedDate = new Date(parseInt(dismissedTimestamp));
      const now = new Date();
      const hoursSinceDismissed = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60);
      
      if (hoursSinceDismissed < data.behavior.cooldownHours) {
        setIsVisible(false);
        return;
      }
    }

    // Show the banner
    setIsVisible(true);
  };

  const trackEvent = async (event: 'view' | 'click' | 'dismiss') => {
    try {
      await fetch(`${API_BASE_URL}/api/promo-bar/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ event })
      });
    } catch (error) {
      logger.error('Error tracking event:', error);
    }
  };

  const handleDismiss = () => {
    if (!promoData) return;

    setIsVisible(false);
    trackEvent('dismiss');
    
    // Track dismissal count
    const dismissalCount = parseInt(localStorage.getItem('promo_bar_dismissal_count') || '0');
    const newDismissalCount = dismissalCount + 1;
    localStorage.setItem('promo_bar_dismissal_count', newDismissalCount.toString());
    
    // If dismissed enough times, don't show again
    if (newDismissalCount >= promoData.behavior.dismissalLimit) {
      // Already at limit, no need to set timestamp
    } else {
      // Set timestamp for temporary dismissal
      localStorage.setItem('promo_bar_dismissed_timestamp', Date.now().toString());
    }
  };

  const handleCTAClick = () => {
    if (!promoData) return;

    trackEvent('click');
    
    if (promoData.behavior.hideAfterClick) {
      localStorage.setItem('promo_bar_clicked', 'true');
    }
  };

  if (!isVisible || !promoData) {
    return null;
  }

  const IconComponent = ICON_MAP[promoData.content.icon] || Zap;
  const { content, visibility, countdown, styling, behavior } = promoData;

  return (
    <div 
      className="relative text-white py-3 px-4 sm:px-6 z-50 shadow-xl overflow-hidden"
      style={{
        background: `linear-gradient(to right, ${styling.backgroundGradient.from}, ${styling.backgroundGradient.via}, ${styling.backgroundGradient.to})`
      }}
    >
      {/* Animated gradient orbs */}
      <div className="absolute top-0 right-0 w-64 h-24 bg-gradient-to-bl from-yellow-400/30 via-orange-400/20 to-transparent rounded-full blur-2xl animate-pulse" style={{ animationDuration: '3s' }} />
      <div className="absolute bottom-0 left-0 w-48 h-24 bg-gradient-to-tr from-red-500/25 to-transparent rounded-full blur-2xl animate-pulse" style={{ animationDuration: '4s' }} />
      
      {/* Matte texture overlay */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />
      
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4 relative z-10">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Icon with glow */}
          {visibility.showIcon && (
            <>
              <div className="relative hidden sm:block">
                <div 
                  className="absolute inset-0 rounded-lg blur-md opacity-60"
                  style={{ background: `linear-gradient(to bottom right, ${styling.accentColor}, ${styling.accentColor})` }}
                />
                <div className="relative w-9 h-9 bg-white/15 backdrop-blur-xl rounded-lg flex items-center justify-center border border-white/30 shadow-lg">
                  <IconComponent 
                    className="w-5 h-5" 
                    style={{ 
                      fill: styling.accentColor,
                      color: styling.accentColor
                    }}
                  />
                </div>
              </div>
              
              {/* Mobile icon */}
              <div className="sm:hidden">
                <IconComponent 
                  className="w-5 h-5 flex-shrink-0 animate-pulse" 
                  style={{ 
                    fill: styling.accentColor,
                    color: styling.accentColor,
                    animationDuration: '2s'
                  }}
                />
              </div>
            </>
          )}
          
          <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
            {/* Text Content */}
            <div className="flex-1 min-w-0">
              {visibility.showHeading && (
                <span 
                  className="block text-sm sm:text-base font-bold"
                  style={{ color: styling.textColor }}
                >
                  {content.heading}
                </span>
              )}
              {visibility.showSubtext && content.subtext && (
                <span 
                  className="hidden sm:block text-xs font-medium mt-0.5"
                  style={{ color: styling.textColor, opacity: 0.9 }}
                >
                  {content.subtext}
                </span>
              )}
            </div>
            
            {/* Countdown Timer */}
            {visibility.showCountdown && countdown.enabled && countdown.endDate && (
              <div className="flex-shrink-0">
                <CountdownTimer
                  endDate={countdown.endDate}
                  timezone={countdown.timezone}
                  labels={countdown.labels}
                  textColor={styling.textColor}
                  accentColor={styling.accentColor}
                  onExpired={() => {
                    if (countdown.hideWhenExpired) {
                      setIsVisible(false);
                    }
                  }}
                />
              </div>
            )}
            
            {/* CTA Button */}
            {visibility.showCTA && (
              <Link 
                to={content.ctaLink}
                onClick={handleCTAClick}
                className="flex-shrink-0 group"
              >
                <div className="relative">
                  {/* Glow behind button */}
                  <div 
                    className="absolute inset-0 rounded-full blur-md opacity-50 group-hover:opacity-75 transition-opacity"
                    style={{ background: `linear-gradient(to right, ${styling.accentColor}, ${styling.accentColor})` }}
                  />
                  
                  <div className="relative bg-white/20 backdrop-blur-xl px-4 sm:px-5 py-2 sm:py-2.5 rounded-full border border-white/40 shadow-lg group-hover:bg-white/30 transition-all flex items-center gap-2">
                    <span className="text-xs sm:text-sm font-bold whitespace-nowrap">
                      {content.ctaText}
                    </span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
        
        {/* Enhanced close button */}
        {behavior.dismissible && (
          <button
            onClick={handleDismiss}
            className="relative p-2 hover:bg-white/15 rounded-full transition-all flex-shrink-0 group/close"
            aria-label="Dismiss announcement"
          >
            <div className="absolute inset-0 bg-white/10 rounded-full opacity-0 group-hover/close:opacity-100 blur-sm transition-opacity" />
            <X className="relative w-4 h-4 sm:w-5 sm:h-5 group-hover/close:rotate-90 transition-transform" />
          </button>
        )}
      </div>
    </div>
  );
}